import * as React from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {
    Avatar,
    Box,
    Chip,
    IconButton,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import {Bot, SendHorizonal, User} from 'lucide-react';
import {StreamChatRequest, TradingAlgo} from "../utils/typesTrading";
import {streamChat} from "../apis/junkyard";
import {AuthContext} from "./AuthContext";

export interface ChatMessage {
    id: string;
    role: 'system' | 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

export interface AIAssistantPanelProps {
    tradingAlgo?: TradingAlgo | null;
    currentCode?: string;
    onProposeEdits?: (proposedCode: string, originalCode: string) => void;
}

const SUGGESTIONS = [
    'Explain this strategy',
    'Add a stop-loss rule',
    'Refactor for readability',
];

// Placeholder conversation so both bubble styles are visible. Replaced by real
// chat data once the assistant backend is wired up.
const createSampleMessages = (): ChatMessage[] => [
    {
        id: 'sample-1',
        role: 'assistant',
        content: "Hi! I'm your trading algorithm assistant. I can edit, explain, debug, and write strategy code.",
        timestamp: new Date(Date.now() - 5 * 60 * 1000),
    },
];

const TypingDots: React.FC = () => (
    <Box sx={{display: 'inline-flex', alignItems: 'flex-end', gap: 0.5, height: 14}} aria-label="Assistant is thinking">
        {[0, 150, 300].map((delay) => (
            <Box
                key={delay}
                sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: 'text.disabled',
                    animation: 'aiTypingBounce 1.2s ease-in-out infinite',
                    animationDelay: `${delay}ms`,
                    '@keyframes aiTypingBounce': {
                        '0%, 60%, 100%': {
                            transform: 'translateY(0)',
                            opacity: 0.4,
                        },
                        '30%': {
                            transform: 'translateY(-4px)',
                            opacity: 1,
                        },
                    },
                }}
            />
        ))}
    </Box>
);

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = (props) => {
    const {tradingAlgo, currentCode, onProposeEdits} = props;
    const [messages, setMessages] = useState<ChatMessage[]>(createSampleMessages);
    const [input, setInput] = useState<string>('');
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    // Accumulates the streamed assistant reply so it can be parsed after the
    // stream ends; `messages` is stale inside the handleSend closure.
    const assistantContentRef = useRef('');
    const authContext = useContext(AuthContext);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages]);

    const extractLastPythonBlock = (text: string): string | null => {
        // Tolerate \r\n and spaces after the fence; ```python-snippet blocks
        // (used for explanatory code) do not match because of the `-`.
        // Array.from instead of spread: the CRA es5 target forbids
        // spread-of-iterator (TS2802).
        const matches = Array.from(text.matchAll(/```python[ \t]*\r?\n([\s\S]*?)```/g));
        const last = matches.at(-1);
        return last ? last[1].replace(/\r\n/g, '\n').trimEnd() : null;
    };


    const buildSystemMessage = (tradingAlgo?: TradingAlgo | null, code?: string) => {
        if (!tradingAlgo) return null;
        const editInstructions =
            'When the user asks you to change their strategy code, return the complete updated file ' +
            'in a single fenced ```python code block after a brief explanation. ' +
            'Never return partial diffs or snippets of the changed file. ' +
            'When you are only explaining or quoting code, not proposing a change, ' +
            'tag the block ```python-snippet instead.';
        return {
            role: 'system' as const,
            content: `You are a trading strategy assistant. The user's active strategy is "${tradingAlgo.strategy_name}":\n\`\`\`python\n${code ?? tradingAlgo.code}\n\`\`\`\n${editInstructions}`,
        };
    };


    const handleSend = async () => {
        const text = input.trim();
        if (!text || isStreaming) return;
        setInput('');

        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: text,
            timestamp: new Date(),
        };
        const assistantId = `assistant-${Date.now()}`;
        const assistantMessage: ChatMessage = {
            id: assistantId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setIsStreaming(true);

        assistantContentRef.current = '';
        let streamFailed = false;
        try {
            const history = [...messages, userMessage].map(({role, content}) => ({role, content}));
            const systemMessage = buildSystemMessage(tradingAlgo, currentCode);
            if (systemMessage) history.unshift(systemMessage);
            const request = {
                access_token: authContext.readAccessToken(),
                messages: history,
                onDelta: (delta) => {
                    assistantContentRef.current += delta;
                    setMessages((prev) =>
                        prev.map((m) => (m.id === assistantId ? {...m, content: m.content + delta} : m)),
                    );
                },
                onError: (error) => {
                    streamFailed = true;
                    setMessages((prev) =>
                        prev.map((m) => (m.id === assistantId ? {...m, content: error} : m)),
                    );
                },
            } as StreamChatRequest;

            await streamChat(request);
            const original = currentCode ?? tradingAlgo?.code ?? '';
            const proposed = streamFailed ? null : extractLastPythonBlock(assistantContentRef.current);
            if (proposed && proposed !== original && onProposeEdits) {
                onProposeEdits(proposed, original);
            }
        } catch (e) {
            setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? {...m, content: `Network error: ${e}`} : m)),
            );
        } finally {
            setIsStreaming(false);
        }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const formatTime = (date: Date): string =>
        date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});

    const renderMessage = (message: ChatMessage) => {
        const isUser = message.role === 'user';
        const isWaiting = !isUser && isStreaming && message.content === '';
        return (
            <Box
                key={message.id}
                sx={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    gap: 1,
                    mb: 2,
                }}
            >
                <Avatar sx={{width: 26, height: 26, bgcolor: isUser ? 'secondary.main' : 'primary.main'}}>
                    {isUser ? <User size={14}/> : <Bot size={14}/>}
                </Avatar>
                <Box sx={{maxWidth: '75%'}}>
                    <Paper
                        elevation={0}
                        sx={{
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            borderTopRightRadius: isUser ? 0 : 2,
                            borderTopLeftRadius: isUser ? 2 : 0,
                            bgcolor: isUser ? 'primary.main' : 'action.hover',
                            color: isUser ? 'primary.contrastText' : 'text.primary',
                        }}
                    >
                        {isWaiting ? (
                            <TypingDots/>
                        ) : (
                            <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                                {message.content}
                            </Typography>
                        )}
                    </Paper>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{display: 'block', px: 0.5, mt: 0.25, textAlign: isUser ? 'right' : 'left'}}
                    >
                        {formatTime(message.timestamp)}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                height: {md: '90vh'},
                flex: {xs: '1 1 auto', md: '0 0 auto'},
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: 1,
                borderColor: 'divider',
                borderRadius: 0,
            }}
        >
            {/* Header */}
            <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Avatar sx={{width: 28, height: 28, bgcolor: 'primary.main'}}>
                        <Bot size={16}/>
                    </Avatar>
                    <Typography variant="h6">AI Assistant</Typography>
                    <Chip
                        label="New!"
                        size="small"
                        variant="outlined"
                        sx={{ml: 'auto', height: 20, fontSize: '0.7rem'}}
                    />
                </Box>
                {tradingAlgo?.strategy_name && (
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                        Context: {tradingAlgo.strategy_name}
                    </Typography>
                )}
            </Box>

            {/* Scrollable message list */}
            <Box ref={scrollRef} sx={{flex: 1, overflowY: 'auto', p: 2}}>
                {messages.map(renderMessage)}
            </Box>

            {/* Suggestions + input */}
            <Box sx={{p: 2, borderTop: 1, borderColor: 'divider'}}>
                <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5}}>
                    {SUGGESTIONS.map((suggestion) => (
                        <Chip
                            key={suggestion}
                            label={suggestion}
                            size="small"
                            onClick={() => setInput(suggestion)}
                            sx={{fontSize: '0.72rem'}}
                        />
                    ))}
                </Box>
                <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 1}}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        size="small"
                        placeholder="Ask about your strategy…"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!input.trim()}
                        aria-label="Send message"
                    >
                        <SendHorizonal size={18}/>
                    </IconButton>
                </Box>
            </Box>
        </Paper>
    );
};